// Shared themed alerts + confirm dialog for dSpr pages.
//
// Drop-in replacement for Swal.fire across all dSpr pages so they match the
// rest of the dSpr theme (navy + orange + clean cards). Two surfaces:
//
//   1. Banner   - inline top-of-page status pill (error / warning / success / info).
//                 Auto-dismisses success/info after 4s; errors stay until clicked
//                 or replaced.
//   2. ConfirmDialog - themed yes/no modal with an orange (or red, danger) confirm
//                 button. Returns a Promise<boolean> so callers can `await` it.
//
// Usage in a page component:
//
//   const { showAlert, showConfirm, AlertsHost } = useDsprAlerts();
//
//   try { await doThing(); showAlert("success", "Done"); }
//   catch (e) { showAlert("error", "Action failed", e.message); }
//
//   if (await showConfirm({ title: "Delete?", danger: true })) doDelete();
//
//   return (<>
//     <AlertsHost />
//     {/* rest of page */}
//   </>);
//
// AlertsHost renders BOTH the banner and the confirm dialog from one host node.

import React, { useCallback, useRef, useState } from "react";

// ── Banner component ─────────────────────────────────────────────────────────
function Banner({ kind, title, message, onDismiss }) {
    if (!message && !title) return null;
    const isWarn = kind === "warning";
    const baseStyle = isWarn
        ? {
            background: "#FFFBEB", border: "1.5px solid #FDE68A",
            borderRadius: 10, padding: "14px 18px", fontSize: 13,
            color: "#92400E", marginBottom: 16,
            display: "flex", alignItems: "flex-start", gap: 8,
            animation: "fadeIn 0.3s ease",
        }
        : {
            background:
                kind === "error"   ? "#FEF2F2" :
                kind === "success" ? "#F0FDF4" :
                                     "#EFF6FF",
            border: `1.5px solid ${
                kind === "error"   ? "#FECACA" :
                kind === "success" ? "#A7F3D0" :
                                     "#BFDBFE"}`,
            color:
                kind === "error"   ? "#DC2626" :
                kind === "success" ? "#047857" :
                                     "#1E40AF",
            borderRadius: 10, padding: "14px 18px",
            fontSize: 13, marginBottom: 16,
            display: "flex", alignItems: "flex-start", gap: 10,
            animation: "fadeIn 0.3s ease",
        };
    const icon =
        kind === "error"   ? "⚠️" :
        kind === "warning" ? "⚠️" :
        kind === "success" ? "✓"  :
                             "ℹ️";
    return (
        <div style={baseStyle} role={kind === "error" ? "alert" : "status"}>
            <span style={{ flexShrink: 0, fontSize: 16, lineHeight: 1 }}>{icon}</span>
            <div style={{ flex: 1 }}>
                {title && <div style={{ fontWeight: 700, marginBottom: message ? 2 : 0 }}>{title}</div>}
                {message && <div style={{ fontWeight: 500 }}>{message}</div>}
            </div>
            {onDismiss && (
                <button type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "inherit", fontSize: 18, padding: 0,
                        lineHeight: 1, marginLeft: 8, fontWeight: 700,
                    }}>×</button>
            )}
        </div>
    );
}

// ── ConfirmDialog component ──────────────────────────────────────────────────
// Used by useDsprAlerts() - caller passes `state` (null or config object) +
// `onClose`. Returns a themed modal-style yes/no dialog matching the
// dSpr theme (orange primary, red for danger).
function ConfirmDialog({ state, onClose }) {
    if (!state) return null;
    const {
        title = "Are you sure?",
        body,
        confirmLabel = "Confirm",
        cancelLabel = "Cancel",
        danger = false,
        icon,
        onConfirm,
        onCancel,
    } = state;
    const handleConfirm = () => {
        try { onConfirm && onConfirm(); }
        finally { onClose && onClose(); }
    };
    const handleCancel = () => {
        try { onCancel && onCancel(); }
        finally { onClose && onClose(); }
    };
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) handleCancel();
    };
    // Default icon picks: danger gets a generic warning ⚠ (not a trash can -
    // closing / disabling / one-way actions are "danger" too, not just
    // deletes). Callers that are ACTUALLY deleting something pass
    // `icon: "🗑️"` explicitly. Default for non-danger stays as a question mark.
    const headerIcon = icon || (danger ? "⚠️" : "❓");
    return (
        <div
            onClick={handleBackdropClick}
            style={{
                position: "fixed", inset: 0, zIndex: 1000,
                background: "rgba(13, 27, 42, 0.55)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20,
                animation: "fadeIn 0.2s ease",
            }}>
            <div style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
                width: "100%",
                maxWidth: 440,
                overflow: "hidden",
                animation: "slideUp 0.25s cubic-bezier(.22,1,.36,1)",
            }}>
                <div style={{
                    padding: "26px 26px 16px",
                    background: danger
                        ? "linear-gradient(180deg, #FEF2F2 0%, #fff 100%)"
                        : "linear-gradient(180deg, #FFF8F5 0%, #fff 100%)",
                }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 14,
                        background: danger
                            ? "linear-gradient(135deg, #FECACA, #FCA5A5)"
                            : "linear-gradient(135deg, #FFF5F0, #FED7C3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 12,
                    }} aria-hidden="true">
                        {/* Emoji rendering uses the text baseline rather than
                            visual center - wrap in a span with lineHeight:1
                            so the glyph sits dead-centre in the tile. */}
                        <span style={{
                            fontSize: 24,
                            lineHeight: 1,
                            display: "inline-block",
                        }}>{headerIcon}</span>
                    </div>
                    <h3 style={{
                        margin: 0, padding: 0,
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 700, fontSize: 18,
                        color: "#0D1B2A", lineHeight: 1.3,
                    }}>{title}</h3>
                    {body && (
                        <p style={{
                            margin: "8px 0 0", padding: 0,
                            fontSize: 13.5, color: "#64748B", lineHeight: 1.55,
                        }}>{body}</p>
                    )}
                </div>
                <div style={{
                    padding: "16px 26px 22px",
                    display: "flex", gap: 10, justifyContent: "flex-end",
                    background: "#fff",
                }}>
                    <button type="button"
                        onClick={handleCancel}
                        style={{
                            padding: "10px 20px",
                            borderRadius: 10,
                            border: "2px solid #E2E8F0",
                            background: "#fff",
                            color: "#475569",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14, fontWeight: 600,
                            cursor: "pointer",
                        }}>
                        {cancelLabel}
                    </button>
                    <button type="button"
                        onClick={handleConfirm}
                        autoFocus
                        style={{
                            padding: "10px 22px",
                            borderRadius: 10,
                            border: "none",
                            background: danger
                                ? "linear-gradient(135deg, #DC2626, #EF4444)"
                                : "linear-gradient(135deg, #E8520A, #FF7434)",
                            color: "#fff",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14, fontWeight: 700,
                            cursor: "pointer",
                            boxShadow: danger
                                ? "0 4px 14px rgba(220, 38, 38, 0.3)"
                                : "0 4px 14px rgba(232, 82, 10, 0.3)",
                        }}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Hook ────────────────────────────────────────────────────────────────────
// One-liner integration. Returns:
//   showAlert(kind, title, message?)  → no return value, auto-dismisses success
//   showConfirm({title, body, confirmLabel?, cancelLabel?, danger?, icon?})
//                                      → Promise<boolean>
//   AlertsHost                         → renders the banner + dialog at the
//                                          configured location in your JSX
export function useDsprAlerts() {
    const [alert, setAlert] = useState(null);       // {kind, title, message} | null
    const [confirm, setConfirm] = useState(null);   // ConfirmDialog state | null
    const resolveRef = useRef(null);

    const showAlert = useCallback((kind, title, message) => {
        setAlert({ kind, title, message });
        if (kind === "success" || kind === "info") {
            setTimeout(() => setAlert(a => (a && a.title === title ? null : a)), 4000);
        }
        // Scroll to top so the banner is visible
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
    }, []);

    const showConfirm = useCallback((opts) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setConfirm({
                ...opts,
                onConfirm: () => { if (resolveRef.current) resolveRef.current(true); },
                onCancel:  () => { if (resolveRef.current) resolveRef.current(false); },
            });
        });
    }, []);

    const closeConfirm = useCallback(() => setConfirm(null), []);
    const dismissAlert = useCallback(() => setAlert(null), []);

    const AlertsHost = useCallback(() => (
        <>
            <Banner
                kind={alert?.kind}
                title={alert?.title}
                message={alert?.message}
                onDismiss={dismissAlert}
            />
            <ConfirmDialog state={confirm} onClose={closeConfirm} />
        </>
    ), [alert, confirm, dismissAlert, closeConfirm]);

    return { showAlert, showConfirm, AlertsHost };
}

// Direct exports for cases that want the components without the hook.
export { Banner, ConfirmDialog };
